import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Heart, Sparkles, MessageCircle, Shield, ChevronRight, Wand2, Gift, Crown } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useLanguage, useTranslation } from "../contexts/LanguageContext";
import { PLAY_STORE_URL, isInstalledApp } from "../lib/products";

// Drapeaux SVG
const FlagFR = () => (
  <svg viewBox="0 0 36 36" className="w-5 h-5">
    <rect fill="#002395" width="12" height="36"/>
    <rect fill="#FFFFFF" x="12" width="12" height="36"/>
    <rect fill="#ED2939" x="24" width="12" height="36"/>
  </svg>
);

const FlagGB = () => (
  <svg viewBox="0 0 36 36" className="w-5 h-5">
    <rect fill="#00247D" width="36" height="36"/>
    <path fill="#FFFFFF" d="M0,0 L36,36 M36,0 L0,36" stroke="#FFFFFF" strokeWidth="6"/>
    <path fill="#CF142B" d="M0,0 L36,36 M36,0 L0,36" stroke="#CF142B" strokeWidth="2"/>
    <path fill="#FFFFFF" d="M18,0 V36 M0,18 H36" stroke="#FFFFFF" strokeWidth="10"/>
    <path fill="#CF142B" d="M18,0 V36 M0,18 H36" stroke="#CF142B" strokeWidth="6"/>
  </svg>
);

export default function Landing() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { language, toggleLanguage } = useLanguage();
  const [isWeb] = useState(() => !isInstalledApp());

  useEffect(() => {
    // Check if already logged in
    const user = localStorage.getItem("user");
    const token = localStorage.getItem("session_token");
    if (user && token) {
      navigate("/discover");
    }
  }, [navigate]);

  return (
    <div className="min-h-screen bg-background overflow-hidden">
      {/* Hero Section */}
      <div className="relative min-h-screen flex flex-col">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1611620005152-bb5732ceff8d?crop=entropy&cs=srgb&fm=jpg&q=85')`,
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-background" />
        </div>

        {/* Header */}
        <header className="relative z-10 p-6 flex justify-between items-center" style={{ paddingTop: 'max(1.5rem, env(safe-area-inset-top))' }}>
          <div className="flex items-center gap-2">
            <img src="/logo-original.png" alt="Love Connect +" className="w-10 h-10" />
            <span className="font-heading text-2xl font-bold text-white">Love Connect +</span>
          </div>
          <div className="flex items-center gap-3">
            {/* Language Toggle */}
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-2 px-3 py-2 bg-white/10 backdrop-blur-sm rounded-full border border-white/20 hover:bg-white/20 transition-all"
              title={t('profile.changeLanguage')}
              data-testid="language-toggle"
            >
              {language === 'fr' ? <FlagGB /> : <FlagFR />}
              <span className="text-sm text-white/80">
                {language === 'fr' ? 'EN' : 'FR'}
              </span>
            </button>
            <Button 
              variant="ghost" 
              className="text-white hover:bg-white/10"
              onClick={() => navigate("/login")}
              data-testid="header-login-btn"
            >
              {t('auth.login')}
            </Button>
          </div>
        </header>

        {/* Hero Content */}
        <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-6 text-center">
          <div className="animate-slide-up">
            <h1 className="font-heading text-5xl md:text-7xl font-bold text-white mb-6 leading-tight flex flex-col justify-center min-h-[12rem] md:min-h-[14rem]">
              {language === 'fr' ? (
                <>Trouvez l'amour<br /><span className="text-gradient">qui vous ressemble</span></>
              ) : (
                <>Find the love<br /><span className="text-gradient">that suits you</span></>
              )}
            </h1>
            <p className="text-lg md:text-xl text-white/80 mb-8 max-w-xl mx-auto font-body min-h-[5rem] flex items-center justify-center">
              {language === 'fr' 
                ? <span>Plus de <span className="font-bold text-white whitespace-nowrap">850 000</span> membres inscrits vous attendent. Rencontrez des personnes authentiques près de chez vous.</span>
                : <span>More than <span className="font-bold text-white whitespace-nowrap">850,000</span> registered members are waiting for you. Meet authentic people near you.</span>
              }
            </p>
            
            {/* Offre de bienvenue */}
            <div className="flex justify-center mb-6">
              <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-yellow-400 to-orange-500 text-black font-bold shadow-[0_0_25px_rgba(250,204,21,0.45)] animate-pulse" data-testid="welcome-credits-badge">
                <Gift className="w-5 h-5" />
                {language === 'fr' ? '5 crédits offerts à l\'inscription' : '5 free credits when you sign up'}
              </div>
            </div>

            {/* App Store Badges — uniquement sur la version web */}
            {isWeb && (
            <div className="flex items-center justify-center gap-4 mb-8" data-testid="store-badges">
              <div className="flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20 opacity-80" data-testid="appstore-badge">
                <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                </svg>
                <div className="text-left">
                  <p className="text-[10px] text-white/60">{language === 'fr' ? 'Bientôt disponible' : 'Coming soon'}</p>
                  <p className="text-sm font-semibold text-white">App Store</p>
                </div>
              </div>
              <a href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20 hover:bg-white/20 transition-colors" data-testid="playstore-badge">
                <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 0 1-.61-.92V2.734a1 1 0 0 1 .609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.198l2.807 1.626a1 1 0 0 1 0 1.73l-2.808 1.626L15.206 12l2.492-2.491zM5.864 2.658L16.802 8.99l-2.303 2.303-8.635-8.635z"/>
                </svg>
                <div className="text-left">
                  <p className="text-[10px] text-white/60">{language === 'fr' ? 'Disponible sur' : 'Available on'}</p>
                  <p className="text-sm font-semibold text-white">Google Play</p>
                </div>
              </a>
            </div>
            )}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg"
                className="bg-primary text-white rounded-full px-8 py-6 font-bold shadow-lg hover:shadow-[0_0_20px_rgba(225,29,72,0.5)] transition-all hover:scale-105 active:scale-95 text-lg"
                onClick={() => navigate("/register")}
                data-testid="hero-cta-btn"
              >
                {language === 'fr' ? 'Commencer maintenant' : 'Get started now'}
                <ChevronRight className="ml-2 w-5 h-5" />
              </Button>
              <Button 
                variant="outline"
                size="lg"
                className="rounded-full px-8 py-6 border-white/30 text-white hover:bg-white/10 text-lg"
                onClick={() => navigate("/login")}
                data-testid="hero-login-btn"
              >
                {language === 'fr' ? "J'ai déjà un compte" : 'I already have an account'}
              </Button>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="relative z-10 pb-8 flex justify-center">
          <div className="animate-bounce">
            <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2">
              <div className="w-1 h-2 bg-white/50 rounded-full" />
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <section className="py-24 px-6 bg-background">
        <div className="max-w-6xl mx-auto">
          {/* AI Feature Highlight */}
          <div className="mb-20 bg-gradient-to-br from-violet-600/20 to-purple-600/20 border border-violet-500/30 rounded-3xl p-8 md:p-12">
            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-violet-600 to-purple-600 flex items-center justify-center shadow-xl shadow-purple-500/30">
                <Wand2 className="w-12 h-12 text-white" />
              </div>
              <div className="flex-1 text-center md:text-left">
                <span className="inline-flex items-center gap-1 mb-3 px-3 py-1 rounded-full bg-gradient-to-r from-yellow-400 to-orange-500 text-black text-xs font-bold" data-testid="ai-premium-badge">
                  <Crown className="w-3.5 h-3.5" />
                  {language === 'fr' ? 'Inclus avec Premium' : 'Included with Premium'}
                </span>
                <h3 className="font-heading text-3xl font-bold text-white mb-3">
                  {language === 'fr' ? 'Recherche IA Révolutionnaire' : 'Revolutionary AI Search'} ✨
                </h3>
                <p className="text-white/80 text-lg mb-4">
                  {language === 'fr' 
                    ? 'Décrivez simplement ce que vous cherchez : "Une femme de 30-40 ans qui aime danser et rigoler" - et l\'IA trouve vos matchs parfaits instantanément !' 
                    : 'Simply describe what you\'re looking for: "A woman 30-40 who loves dancing and laughing" - and AI finds your perfect matches instantly!'}
                </p>
                <Button 
                  onClick={() => navigate("/register")}
                  className="bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-full px-6"
                >
                  {language === 'fr' ? 'Essayer maintenant' : 'Try it now'}
                  <Sparkles className="ml-2 w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>

          <h2 className="font-heading text-3xl md:text-5xl font-semibold text-center text-white mb-16">
            {language === 'fr' ? 'Pourquoi ' : 'Why '}<span className="text-gradient">Love Connect +</span> ?
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            <FeatureCard 
              icon={<Heart className="w-8 h-8" />}
              title={language === 'fr' ? 'Matchs de qualité' : 'Quality matches'}
              description={language === 'fr' 
                ? 'Notre algorithme vous propose des profils compatibles avec vos préférences et centres d\'intérêt.'
                : 'Our algorithm suggests profiles compatible with your preferences and interests.'
              }
            />
            <FeatureCard 
              icon={<MessageCircle className="w-8 h-8" />}
              title={language === 'fr' ? 'Messagerie instantanée' : 'Instant messaging'}
              description={language === 'fr'
                ? 'Discutez en temps réel avec vos matchs et créez des connexions authentiques.'
                : 'Chat in real time with your matches and create authentic connections.'
              }
            />
            <FeatureCard 
              icon={<Shield className="w-8 h-8" />}
              title={language === 'fr' ? 'Profils vérifiés' : 'Verified profiles'}
              description={language === 'fr'
                ? 'Tous nos utilisateurs sont vérifiés pour garantir des rencontres en toute confiance.'
                : 'All our users are verified to ensure trustworthy meetings.'
              }
            />
          </div>
        </div>
      </section>

      {/* Premium Section */}
      <section className="py-24 px-6 relative overflow-hidden">
        <div className="absolute inset-0 gradient-brand opacity-10" />
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-secondary/20 rounded-full mb-6">
            <Sparkles className="w-5 h-5 text-secondary" />
            <span className="text-secondary font-ui font-medium">
              {language === 'fr' ? 'Fonctionnalités Premium' : 'Premium Features'}
            </span>
          </div>
          <h2 className="font-heading text-3xl md:text-5xl font-semibold text-white mb-6">
            {language === 'fr' ? 'Boostez vos rencontres' : 'Boost your dating'}
          </h2>
          <p className="text-muted-foreground text-lg mb-8 max-w-2xl mx-auto">
            {language === 'fr'
              ? 'Avec nos crédits, accédez aux fonctionnalités exclusives : Super Likes, Boosts de profil et découvrez qui vous a liké !'
              : 'With our credits, access exclusive features: Super Likes, Profile Boosts and see who liked you!'
            }
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <PremiumFeature label="Super Like" credits="5" language={language} />
            <PremiumFeature label={language === 'fr' ? 'Boost Profil' : 'Profile Boost'} credits="20" language={language} />
            <PremiumFeature label={language === 'fr' ? 'Voir les Likes' : 'See Likes'} credits="5" language={language} />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-white mb-6">
            {language === 'fr' ? "Prêt à trouver l'amour ?" : 'Ready to find love?'}
          </h2>
          <p className="text-muted-foreground mb-8">
            {language === 'fr'
              ? 'Rejoignez notre communauté de célibataires et commencez votre histoire.'
              : 'Join our community of singles and start your story.'
            }
          </p>
          <Button 
            size="lg"
            className="bg-primary text-white rounded-full px-10 py-6 font-bold shadow-lg hover:shadow-[0_0_20px_rgba(225,29,72,0.5)] transition-all hover:scale-105"
            onClick={() => navigate("/register")}
            data-testid="cta-register-btn"
          >
            {language === 'fr' ? 'Créer mon profil gratuitement' : 'Create my profile for free'}
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-white/10">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-2">
            <img src="/logo-original.png" alt="Love Connect +" className="w-8 h-8" />
            <span className="font-heading text-lg font-bold text-white">Love Connect +</span>
          </div>
          {isWeb && <div className="flex items-center gap-4 text-sm" data-testid="footer-legal-links">
            <Link to="/privacy-policy" className="text-muted-foreground hover:text-white" data-testid="footer-privacy-link">{language === 'fr' ? 'Confidentialité' : 'Privacy'}</Link>
            <Link to="/terms" className="text-muted-foreground hover:text-white" data-testid="footer-terms-link">{language === 'fr' ? 'CGU & Mentions légales' : 'Terms & Legal'}</Link>
            <a href="/child-safety.html" className="text-muted-foreground hover:text-white" data-testid="footer-child-safety-link">{language === 'fr' ? 'Sécurité des enfants' : 'Child Safety'}</a>
          </div>}
          <p className="text-muted-foreground text-sm">
            © {new Date().getFullYear()} Love Connect +. {language === 'fr' ? 'Tous droits réservés.' : 'All rights reserved.'}
          </p>
        </div>
      </footer>
    </div>
  );
}

function FeatureCard({ icon, title, description }) {
  return (
    <div className="bg-card/50 backdrop-blur-sm border border-white/5 p-8 rounded-3xl hover:border-primary/50 transition-all group">
      <div className="w-16 h-16 rounded-2xl gradient-brand flex items-center justify-center text-white mb-6 group-hover:glow-primary transition-all">
        {icon}
      </div>
      <h3 className="font-heading text-xl font-semibold text-white mb-3">{title}</h3>
      <p className="text-muted-foreground">{description}</p>
    </div>
  );
}

function PremiumFeature({ label, credits, language }) {
  return (
    <div className="bg-card/50 backdrop-blur-sm border border-white/10 px-6 py-4 rounded-2xl flex items-center gap-3">
      <span className="text-white font-medium">{label}</span>
      <span className="px-3 py-1 bg-secondary/20 text-secondary rounded-full text-sm font-bold">
        {credits} {language === 'fr' ? 'crédits' : 'credits'}
      </span>
    </div>
  );
}
